import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// Paleta estable para tipos "nuevos" que ChatGPT u otros usuarios inventen.
const TYPE_PALETTE = [
  "bg-purple-100 text-purple-800",
  "bg-blue-100 text-blue-800",
  "bg-pink-100 text-pink-800",
  "bg-red-100 text-red-800",
  "bg-amber-100 text-amber-800",
  "bg-emerald-100 text-emerald-800",
  "bg-indigo-100 text-indigo-800",
  "bg-teal-100 text-teal-800",
  "bg-orange-100 text-orange-800",
  "bg-rose-100 text-rose-800",
];

// Colores fijos para los built-in (consistencia visual con la versión anterior).
const BUILTIN_CLS = {
  productora:   "bg-purple-100 text-purple-800",
  distribuidor: "bg-blue-100 text-blue-800",
  canal:        "bg-blue-100 text-blue-800",
  festival:     "bg-pink-100 text-pink-800",
  financiador:  "bg-red-100 text-red-800",
  prensa:       "bg-amber-100 text-amber-800",
  otro:         "bg-stone-100 text-stone-700",
};

function hashSlug(slug) {
  let h = 0;
  for (let i = 0; i < (slug || "").length; i++) h = ((h << 5) - h) + slug.charCodeAt(i) | 0;
  return Math.abs(h);
}

function typeMeta(slug, catalog) {
  const cat = catalog.find((t) => t.slug === slug);
  const label = cat?.label || (slug || "otro").replace(/_/g, " ").replace(/^./, s => s.toUpperCase());
  const cls = BUILTIN_CLS[slug] || TYPE_PALETTE[hashSlug(slug) % TYPE_PALETTE.length];
  return { label, cls };
}

export default function ContactosPage() {
  const [tab, setTab] = useState("companies");
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [types, setTypes] = useState([]);  // catálogo dinámico de tipos
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("todos");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p, t] = await Promise.all([
        api.get("/companies"),
        api.get("/contacts"),
        api.get("/company-types"),
      ]);
      setCompanies(c.data.companies || []);
      setContacts(p.data.contacts || []);
      setTypes(t.data.types || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredCompanies = useMemo(() => companies.filter(c => {
    if (typeF !== "todos" && c.type !== typeF) return false;
    if (q.trim()) {
      const s = q.toLowerCase();
      const hay = [c.name, ...(c.tags || []), c.notes, c.city].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [companies, q, typeF]);

  const filteredContacts = useMemo(() => contacts.filter(c => {
    if (q.trim()) {
      const s = q.toLowerCase();
      const hay = [
        c.name, c.first_name, c.last_name, c.role, c.email,
        c.phone, c.company_website, c.notes, ...(c.tags || []),
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [contacts, q]);

  const companyName = (cid) => companies.find(x => x.id === cid)?.name || "";

  return (
    <div className="min-h-screen bg-gb-bg p-6" data-testid="contactos-page">
      <header className="mb-5 flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#26775E] font-semibold">Compartido</p>
          <h1 className="font-serif text-3xl mt-1">Empresas y contactos</h1>
          <p className="text-sm text-stone-600 mt-1">Directorio del estudio. Vinculado a proyectos, festivales y convocatorias.</p>
        </div>
        <button data-testid="contactos-new"
                onClick={() => setEditing(tab === "contacts"
                  ? { _isContact: true, tags: [], first_name: "", last_name: "" }
                  : { type: "otro", tags: [] })}
                className="rounded-full bg-[#941620] hover:bg-[#7a1119] text-white px-4 py-1.5 text-[11px] uppercase tracking-widest font-semibold">
          + Nueva {tab === "companies" ? "empresa" : "persona"}
        </button>
      </header>

      <div className="inline-flex rounded-full border border-stone-300 bg-white p-1 text-[11px] uppercase tracking-[0.25em] mb-4">
        <button onClick={() => setTab("companies")} data-testid="contactos-tab-companies"
                className={`rounded-full px-4 py-1.5 ${tab === "companies" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-500"}`}>
          Empresas ({companies.length})
        </button>
        <button onClick={() => setTab("contacts")} data-testid="contactos-tab-contacts"
                className={`rounded-full px-4 py-1.5 ${tab === "contacts" ? "bg-stone-900 text-[#FAFAF6]" : "text-stone-500"}`}>
          Personas ({contacts.length})
        </button>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="contactos-search"
                 className="rounded-md border border-stone-300 px-3 py-1.5 text-sm w-64" />
          {tab === "companies" && (
            <select value={typeF} onChange={(e) => setTypeF(e.target.value)}
                    data-testid="contactos-filter-type"
                    className="rounded-md border border-stone-300 px-2 py-1.5 text-sm">
              <option value="todos">Todos los tipos</option>
              {types.map(t => <option key={t.slug} value={t.slug}>{t.label}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading && <div className="text-center text-stone-400 py-10">Cargando…</div>}

      {!loading && tab === "companies" && (
        <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-widest text-stone-500">
                <th className="px-4 py-2.5">Empresa</th>
                <th className="px-3 py-2.5">Tipo</th>
                <th className="px-3 py-2.5">Ubicación</th>
                <th className="px-3 py-2.5 text-center">Contactos</th>
                <th className="px-3 py-2.5">Web</th>
                <th className="px-3 py-2.5 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 && (
                <tr><td colSpan={6} className="p-10 text-center text-stone-500">
                  {companies.length === 0 ? "Aún no hay empresas. Pulsa \"Nueva empresa\" para empezar." : "Sin resultados."}
                </td></tr>
              )}
              {filteredCompanies.map(c => {
                const meta = typeMeta(c.type, types);
                return (
                  <tr key={c.id} onClick={() => setSelected(c)}
                      data-testid={`company-row-${c.id}`}
                      className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer">
                    <td className="px-4 py-3">
                      <p className="font-serif text-[15px] font-medium">{c.name}</p>
                      {c.notes && <p className="text-[11px] text-stone-500 line-clamp-1">{c.notes.slice(0, 60)}</p>}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>
                    </td>
                    <td className="px-3 py-3 text-[12px] text-stone-700">{c.city ? `${c.city} · ` : ""}{c.country || "—"}</td>
                    <td className="px-3 py-3 text-center font-semibold">{c.contacts_count || 0}</td>
                    <td className="px-3 py-3 text-[12px]">
                      {c.website ? <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#941620] hover:underline">Web →</a> : <span className="text-stone-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                              data-testid={`company-edit-${c.id}`}
                              className="rounded-full border border-stone-300 px-2 py-0.5 text-[11px] hover:border-[#941620]">✎</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && tab === "contacts" && (
        <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-widest text-stone-500">
                <th className="px-4 py-2.5">Nombre</th>
                <th className="px-3 py-2.5">Apellidos</th>
                <th className="px-3 py-2.5">Cargo</th>
                <th className="px-3 py-2.5">Empresa</th>
                <th className="px-3 py-2.5">Email</th>
                <th className="px-3 py-2.5">Teléfono</th>
                <th className="px-3 py-2.5">Web empresa</th>
                <th className="px-3 py-2.5">Etiquetas</th>
                <th className="px-3 py-2.5 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.length === 0 && (
                <tr><td colSpan={9} className="p-10 text-center text-stone-500">
                  {contacts.length === 0 ? "Aún no hay personas. Pulsa \"Nueva persona\"." : "Sin resultados."}
                </td></tr>
              )}
              {filteredContacts.map(c => {
                const first = c.first_name || (c.name || "").split(" ")[0] || "";
                const last = c.last_name ?? (c.name || "").split(" ").slice(1).join(" ");
                const web = c.company_website || companies.find(x => x.id === c.company_id)?.website || "";
                const companyName = companies.find(x => x.id === c.company_id)?.name;
                return (
                <tr key={c.id} data-testid={`contact-row-${c.id}`} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-3">
                    <p className="font-medium">{first || <span className="text-stone-400">—</span>}</p>
                  </td>
                  <td className="px-3 py-3 text-[13px] text-stone-700">{last || <span className="text-stone-400">—</span>}</td>
                  <td className="px-3 py-3 text-[12px]">
                    {c.role ? (
                      <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] text-stone-800">{c.role}</span>
                    ) : <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-[12px] text-stone-700">{companyName || <span className="text-stone-300">—</span>}</td>
                  <td className="px-3 py-3 text-[12px]">{c.email ? <a href={`mailto:${c.email}`} className="text-[#941620] hover:underline">{c.email}</a> : <span className="text-stone-300">—</span>}</td>
                  <td className="px-3 py-3 text-[12px] text-stone-700">{c.phone || <span className="text-stone-300">—</span>}</td>
                  <td className="px-3 py-3 text-[12px]">
                    {web ? (
                      <a href={web.startsWith("http") ? web : `https://${web}`}
                         target="_blank" rel="noopener noreferrer"
                         className="text-[#941620] hover:underline">{web.replace(/^https?:\/\//, "").slice(0, 24)}</a>
                    ) : <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-3 py-3">
                    {(c.tags || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).slice(0, 3).map((t, i) => (
                          <span key={i} className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] text-stone-700">{t}</span>
                        ))}
                        {(c.tags || []).length > 3 && <span className="text-[10px] text-stone-500">+{c.tags.length - 3}</span>}
                      </div>
                    ) : <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => setEditing({ ...c, _isContact: true })}
                            data-testid={`contact-edit-${c.id}`}
                            className="rounded-full border border-stone-300 px-2 py-0.5 text-[11px] hover:border-[#941620]">✎</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && <CompanyDetailModal
        company={selected}
        allContacts={contacts.filter(x => x.company_id === selected.id)}
        types={types}
        onClose={() => setSelected(null)}
        onEdit={() => { setEditing(selected); setSelected(null); }}
        onAddPerson={(comp) => {
          setEditing({
            _isContact: true,
            _fromCompany: true,
            tags: [],
            first_name: "",
            last_name: "",
            company_id: comp.id,
            company_website: comp.website || "",
          });
          setSelected(null);
        }}
        onEditPerson={(person) => {
          setEditing({ ...person, _isContact: true });
          setSelected(null);
        }}
      />}
      {editing && <EditModal item={editing} companies={companies} types={types} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CompanyDetailModal({ company, allContacts, types, onClose, onEdit, onEditPerson, onAddPerson }) {
  const meta = typeMeta(company.type, types || []);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" data-testid="company-detail-modal">
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-stone-200 bg-gradient-to-br from-[#941620] to-[#7a1119] px-6 py-4 text-white">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/70">{meta.label}</p>
            <h2 className="mt-0.5 font-serif text-2xl">{company.name}</h2>
            <p className="text-[11px] text-white/80 mt-0.5">{company.city ? `${company.city} · ` : ""}{company.country || ""}{company.website ? ` · ${company.website}` : ""}</p>
          </div>
          <div className="flex gap-1">
            <button onClick={onEdit} className="rounded-full bg-white/20 hover:bg-white/30 px-3 py-1 text-[11px] uppercase tracking-widest font-semibold">✎ Editar</button>
            <button onClick={onClose} className="rounded-full bg-white/20 hover:bg-white/30 px-2.5 py-0.5 text-lg">×</button>
          </div>
        </div>
        <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                Personas de contacto · {allContacts.length}
              </p>
              <button onClick={() => onAddPerson(company)}
                      data-testid="company-add-person"
                      className="rounded-full bg-[#941620] hover:bg-[#7a1119] text-white px-3 py-1 text-[10px] uppercase tracking-widest font-semibold">
                + Añadir persona
              </button>
            </div>
            {allContacts.length === 0 && (
              <p className="text-[12px] text-stone-400 italic">
                Aún no hay personas asociadas. Pulsa "Añadir persona" para empezar.
              </p>
            )}
            <div className="space-y-1.5">
              {allContacts.map(c => {
                const initials = `${(c.first_name || c.name || "")[0] || ""}${(c.last_name || "")[0] || ""}`.toUpperCase() || "?";
                return (
                  <button key={c.id} onClick={() => onEditPerson(c)}
                          data-testid={`company-person-${c.id}`}
                          className="w-full flex items-center gap-3 rounded-md bg-stone-50 hover:bg-stone-100 p-3 text-left transition">
                    <span className="w-9 h-9 rounded-full bg-stone-800 text-white flex items-center justify-center text-[11px] font-bold">{initials}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium">{c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim()}</p>
                      <div className="flex items-center gap-2 text-[11px]">
                        {c.role ? (
                          <span className="rounded-full bg-white border border-stone-300 px-2 py-0.5 font-medium text-stone-700">{c.role}</span>
                        ) : (
                          <span className="italic text-stone-400">— sin cargo —</span>
                        )}
                        {c.email && <span className="text-stone-500 truncate">{c.email}</span>}
                        {c.phone && <span className="text-stone-500">· {c.phone}</span>}
                      </div>
                    </div>
                    <span className="text-stone-400 text-[11px]">✎</span>
                  </button>
                );
              })}
            </div>
          </div>
          {company.notes && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Notas</p>
              <p className="text-[13px] text-stone-700 italic whitespace-pre-wrap">{company.notes}</p>
            </div>
          )}
          {(company.tags || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Etiquetas</p>
              <div className="flex flex-wrap gap-1">
                {company.tags.map((t, i) => <span key={i} className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] text-stone-700">{t}</span>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EditModal({ item, companies, types, onClose, onSaved }) {
  const isContact = !!item._isContact || (item.role !== undefined && item.name !== undefined && !item.type);
  const [f, setF] = useState(isContact
    ? {
        first_name: item.first_name ?? ((item.name || "").split(" ")[0] || ""),
        last_name:  item.last_name  ?? ((item.name || "").split(" ").slice(1).join(" ") || ""),
        role: item.role || "",
        email: item.email || "",
        phone: item.phone || "",
        company_id: item.company_id || "",
        company_website: item.company_website || "",
        tags: (item.tags || []).join(", "),
        notes: item.notes || "",
      }
    : { name: item.name || "", type: item.type || "otro", website: item.website || "", country: item.country || "", city: item.city || "", tags: (item.tags || []).join(", "), notes: item.notes || "" }
  );
  const [saving, setSaving] = useState(false);

  // Auto-rellenar web de empresa cuando se elige una del catálogo
  const linkCompany = (cid) => {
    const comp = companies.find(x => x.id === cid);
    setF((prev) => ({
      ...prev,
      company_id: cid,
      company_website: prev.company_website?.trim() ? prev.company_website : (comp?.website || ""),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isContact) {
        const payload = {
          first_name: f.first_name.trim(),
          last_name: f.last_name.trim(),
          role: f.role.trim(),
          email: f.email.trim(),
          phone: f.phone.trim(),
          company_id: f.company_id || null,
          company_website: f.company_website.trim(),
          tags: f.tags.split(",").map(x => x.trim()).filter(Boolean),
          notes: f.notes,
        };
        if (item.id) await api.patch(`/contacts/${item.id}`, payload);
        else await api.post("/contacts", payload);
      } else {
        const payload = { ...f, tags: f.tags.split(",").map(x => x.trim()).filter(Boolean) };
        if (item.id) await api.patch(`/companies/${item.id}`, payload);
        else await api.post("/companies", payload);
      }
      onSaved();
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!item.id || !window.confirm("¿Borrar?")) return;
    if (isContact) await api.delete(`/contacts/${item.id}`);
    else await api.delete(`/companies/${item.id}`);
    onSaved();
  };

  const canSave = isContact
    ? !!(f.first_name?.trim() || f.last_name?.trim())
    : !!f.name;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" data-testid="contactos-edit-modal">
      <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-stone-200 bg-gradient-to-br from-[#941620] to-[#7a1119] px-6 py-4 text-white flex justify-between">
          <h2 className="font-serif text-xl">{item.id ? "Editar" : "Nueva"} {isContact ? "persona" : "empresa"}</h2>
          <button onClick={onClose} className="rounded-full bg-white/20 px-2.5 py-0.5 text-lg">×</button>
        </div>
        <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto">
          {isContact ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Nombre</label>
                  <input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })}
                         data-testid="edit-first-name"
                         className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Apellidos</label>
                  <input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })}
                         data-testid="edit-last-name"
                         className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Cargo</label>
                  <input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}
                         data-testid="edit-role"
                         placeholder="Director, productora, prensa…"
                         className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                    Empresa {item._fromCompany && <span className="text-[9px] normal-case text-[#941620] font-bold ml-1">· vinculada</span>}
                  </label>
                  <select value={f.company_id} onChange={(e) => linkCompany(e.target.value)}
                          disabled={!!item._fromCompany}
                          data-testid="edit-company"
                          className="w-full rounded-md border border-stone-300 px-2 py-1.5 text-sm disabled:bg-stone-50">
                    <option value="">— ninguna —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Email</label>
                  <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                         data-testid="edit-email"
                         className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Teléfono</label>
                  <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                         data-testid="edit-phone"
                         className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Web de empresa</label>
                <input value={f.company_website} onChange={(e) => setF({ ...f, company_website: e.target.value })}
                       data-testid="edit-company-website"
                       placeholder="https://..."
                       className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Etiquetas (separadas por coma)</label>
                <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })}
                       data-testid="edit-tags"
                       placeholder="guionista, cannes, lanzadera…"
                       className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Nombre</label>
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                       data-testid="edit-name"
                       className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Tipo</label>
                  <input list="company-types-list" value={f.type}
                         onChange={(e) => setF({ ...f, type: e.target.value })}
                         data-testid="edit-type"
                         placeholder="productora, agencia_prensa…"
                         className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                  <datalist id="company-types-list">
                    {(types || []).map(t => <option key={t.slug} value={t.slug}>{t.label}</option>)}
                  </datalist>
                  <p className="mt-0.5 text-[10px] text-stone-400">
                    Elige uno o escribe uno nuevo (p.ej. "colaborador"). Se añade al catálogo.
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Web</label>
                  <input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">País</label>
                  <input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Ciudad</label>
                  <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Etiquetas (separadas por coma)</label>
                <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
              </div>
            </>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Notas</label>
            <textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm" />
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-6 py-3">
          {item.id ? <button onClick={remove} className="text-[11px] uppercase tracking-widest text-rose-700 hover:underline font-semibold">🗑 Borrar</button> : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-full border border-stone-300 px-4 py-1.5 text-[11px] uppercase tracking-widest font-semibold">Cancelar</button>
            <button onClick={save} disabled={saving || !canSave}
                    data-testid="edit-save"
                    className="rounded-full bg-[#941620] hover:bg-[#7a1119] text-white px-5 py-1.5 text-[11px] uppercase tracking-widest font-semibold disabled:opacity-50">
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
